import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { CaptureProblem } from '@/core/recording/session';
import { cn } from '@/lib/cn';
import { recordingElapsed, recordingSize, type RecordingProblem } from '@/state/recording-store';

/** A live camera or screen stream. Selfie previews are mirrored, as people expect; recordings are not. */
export function LiveVideo({ stream, mirrored = false, className, style, label }: { readonly stream: MediaStream; readonly mirrored?: boolean; readonly className?: string; readonly style?: CSSProperties; readonly label?: string }) {
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    element.srcObject = stream;
    void element.play().catch(() => undefined);
    return () => {
      element.srcObject = null;
    };
  }, [stream]);
  return (
    <video
      ref={video}
      muted
      playsInline
      autoPlay
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('object-cover', mirrored && '-scale-x-100', className)}
      style={style}
    />
  );
}

/**
 * A five-bar level meter. It reads the level on every animation frame and
 * writes straight to the DOM, so a moving meter never re-renders React.
 */
export function LevelMeter({ level, label, className }: { readonly level: () => number; readonly label: string; readonly className?: string }) {
  const bars = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let frame = 0;
    let smoothed = 0;
    const tick = () => {
      // Quick to rise, slow to fall, like a hardware meter.
      const next = level();
      smoothed = next > smoothed ? next : smoothed * 0.88 + next * 0.12;
      const lit = Math.round(smoothed * 5);
      bars.current?.querySelectorAll('span').forEach((bar, index) => bar.toggleAttribute('data-on', index < lit));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [level]);
  return (
    <span ref={bars} role="img" aria-label={label} className={cn('inline-flex h-3.5 items-end gap-[2px]', className)}>
      {[0.35, 0.5, 0.65, 0.82, 1].map((height) => (
        <span
          key={height}
          className="w-[3px] rounded-full bg-current opacity-20 transition-opacity duration-75 data-[on]:opacity-100"
          style={{ height: `${height * 100}%` }}
        />
      ))}
    </span>
  );
}

/** Elapsed seconds and bytes of the running recording, refreshed a few times a second. */
export function useRecordingProgress(active: boolean): { readonly elapsed: number; readonly size: number } {
  const [progress, setProgress] = useState({ elapsed: recordingElapsed(), size: recordingSize() });
  useEffect(() => {
    if (!active) return;
    const update = () => setProgress({ elapsed: recordingElapsed(), size: recordingSize() });
    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [active]);
  return progress;
}

type DeviceProblemKey = 'denied' | 'not-found' | 'in-use' | 'other';

/** Device problems the setup explains; anything else reads as "couldn't be started". */
export const deviceProblemKey = (problem: CaptureProblem): DeviceProblemKey =>
  problem === 'denied' || problem === 'not-found' || problem === 'in-use' ? problem : 'other';

/** The translation key that explains a recording problem. */
export function problemKey(problem: RecordingProblem):
  | `record.problems.${'systemDenied' | 'screenUnsupported' | 'screenFailed' | 'noEncoder' | 'storageFull' | 'prepareFailed' | 'unknown'}`
  | `record.${'microphone' | 'camera'}Problem.${DeviceProblemKey}` {
  switch (problem.kind) {
    case 'no-encoder':
      return 'record.problems.noEncoder';
    case 'storage-full':
      return 'record.problems.storageFull';
    case 'prepare-failed':
      return 'record.problems.prepareFailed';
    case 'unknown':
      return 'record.problems.unknown';
    case 'capture':
      if (problem.source === 'screen') {
        if (problem.problem === 'system-denied') return 'record.problems.systemDenied';
        return problem.problem === 'unsupported' ? 'record.problems.screenUnsupported' : 'record.problems.screenFailed';
      }
      return `record.${problem.source}Problem.${deviceProblemKey(problem.problem)}`;
  }
}
